import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Pagination from "react-js-pagination";
import {
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableContainer,
  TableFooter,
} from "@material-ui/core";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// component
import CouponListTableMore from "./CouponListTableMore";
import EditContextPopup from "./editContextPopup";

// 전역 상태 관리
import { useRecoilState } from "recoil";
import { dataChangeState, rowDataChangeState, paidSeqState  } from "../../../recoil/changePlanData";

// css
import styled from "styled-components";
import P from "../../../styled-components/planStyle"
import "../../../components/Style/Table.css";
import sortAsc from "../../../images/ico_filter_up.svg"
import sortDesc from "../../../images/ico_filter_down.svg"

export default function CouponListTable({filterKeyword})  {

  const searchTargets = ["grant_code", "note"];

  // 전체 데이터
  const [totalData, setTotalData] = useState([]);

  // pagination
  const [dataCnt, setDataCnt] = useState(0);
  const [page, setPage] = useState(1);
  const [currentPost, setCurrentPost] = useState([]);
  const postPerPage = 10;
  const indexOfLastPost = page * postPerPage;
  const indexOfFirstPost = indexOfLastPost - postPerPage;

  // edit popup
  const [popupOpen, setPopupOpen] = useState(false);
  const [paidSeq, setPaidSeq] = useState(0);
  const [paidNote, setPaidNote] = useState("");
  const [editPart, setEditPart] = useState("");
  const [prevData, setPrevData] = useState(null);

  // reload를 위한 전역 상태 변수
  const [rowdataChange, setRowDataChange] = useRecoilState(rowDataChangeState);
  const [paidSeqSelect, setPaidSeqSelect] = useRecoilState(paidSeqState);
  const [dataChange, setDataChange] = useRecoilState(dataChangeState);

  // filter
  const [memberSelectedValue, setMemberSelectedValue] = useState(1);
  const [filterChange, setFilterChange] = useState(false);

  // 자식 행 표시
  const [expandedRows, setExpandedRows] = useState({});
  
  // 엑셀 다운로드 로딩 상태
  const [isExcelLoading, setIsExcelLoading] = useState(false);

  // 엑셀 다운로드 함수
  const downloadExcel = async () => {
    setIsExcelLoading(true);
    try {
      // 필터링된 데이터 또는 전체 데이터 사용
    let dataToExport = filterKeyword && filterKeyword !== '' 
      ? totalData.filter((item) => {
          return searchTargets.some((target) => {
            if (item[target] && typeof item[target] === 'string') {
              return item[target].toLowerCase().includes(filterKeyword.toLowerCase());
            }
          })
        })
      : totalData;

    // 종료된 항목 제외
    dataToExport = dataToExport.filter((item) => {
      const isValidDate = (dateStr) =>
        dateStr && !isNaN(new Date(dateStr).getTime());
      const isEnd = isValidDate(item.end_date)
        ? new Date(item.end_date.split("T")[0]) < new Date()
        : false;
      return !isEnd; // 종료되지 않은 항목만 포함
    });

    // 오늘 날짜 계산
    const currentDate = new Date();
    const todayStr = currentDate.getFullYear() + 
                   String(currentDate.getMonth() + 1).padStart(2, '0') + 
                   String(currentDate.getDate()).padStart(2, '0');

         // 각 항목의 상세 정보 가져오기
     const excelDataWithDetails = [];
     let rowNumber = 1; // 행 번호를 별도로 관리
     
     for (let i = 0; i < dataToExport.length; i++) {
      const item = dataToExport[i];
      
      // 종료일까지 남은 일수 계산
      const endDate = new Date(item.end_date?.split("T")[0]);
      const daysLeft = Math.ceil((endDate - currentDate) / (1000 * 60 * 60 * 24));
      
      // 상세 정보 가져오기
      let detailedData = [];
      try {
        detailedData = await getMoreInfo(item.paid_seq);
      } catch (error) {
        console.error(`상세 정보 가져오기 실패 (paid_seq: ${item.paid_seq}):`, error);
        detailedData = [];
      }
      
             // 기본 정보
       const baseInfo = {
         '구독 결제 번호': item.paid_seq,
         '상품명': item.note,
         '등록 코드': item.grant_code || '없음',
         '사용 인원': `${item.use_member_cnt}/${item.usable_member_cnt}`,
         '가격': item.paid_price.toLocaleString(),
         '시작일': item.issue_date ? item.issue_date.slice(0, 10) : '없음',
         '종료일': item.end_date?.slice(0, 10) ?? '없음',
         '남은 일수': daysLeft > 0 ? `${daysLeft}일` : '만료',
         '담당자': item.manager || '없음',
         '담당자 이메일': item.manager_email || '없음',
         '담당자 연락처': item.manager_phone || '없음',
         '특이사항': item.comment || '없음'
       };
      
             // 상세 정보가 있는 경우
       if (detailedData && detailedData.length > 0) {
         // 각 멤버별로 행 추가 (멤버 정보가 있는 경우만)
         detailedData.forEach((member, memberIndex) => {
           // 멤버 정보가 모두 없는 경우 제외
           if (member.name || member.email || member.org_name) {
             const memberInfo = {
               '번호': rowNumber++,
               ...baseInfo,
               '멤버 회사': member.org_name || '없음',
               '멤버 이름': member.name || '없음',
               '멤버 이메일': member.email || '없음'
             };
             excelDataWithDetails.push(memberInfo);
           }
         });
       }
       
       // 멤버 정보가 없는 경우 기본 정보만 추가
       if (!detailedData || detailedData.length === 0 || 
           !detailedData.some(member => member.name || member.email || member.org_name)) {
         excelDataWithDetails.push({
           '번호': rowNumber++,
           ...baseInfo,
           '멤버 회사': '',
           '멤버 이름': '',
           '멤버 이메일': ''
         });
       }
    }

    // 워크북 생성
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelDataWithDetails);

    // 디데이 기준일을 첫 번째 행에 추가
    const headerRow = [['남은 일수 기준일: ' + todayStr.slice(0, 4) + '-' + todayStr.slice(4, 6) + '-' + todayStr.slice(6, 8)]];
    const emptyRow = [['']];
    
    // 컬럼 헤더 추가
    const columnHeaders = [['번호', '구독 결제 번호', '상품명', '등록 코드', '멤버 회사', '멤버 이름', '가격', '시작일', '종료일', '남은 일수', '사용 인원', '담당자', '담당자 연락처', '멤버 이메일', '담당자 이메일', '특이사항']];
    
    // excelDataWithDetails를 2차원 배열로 변환
    const excelDataArray = excelDataWithDetails.map(item => [
      item['번호'],
      item['구독 결제 번호'],
      item['상품명'],
      item['등록 코드'],
      item['멤버 회사'],
      item['멤버 이름'],
      Number(item['가격'].replace(/,/g, '')), // 가격을 숫자로 변환
      item['시작일'],
      item['종료일'],
      item['남은 일수'],
      item['사용 인원'],
      item['담당자'],
      item['담당자 연락처'],
      item['멤버 이메일'],
      item['담당자 이메일'],
      item['특이사항']
    ]);
    
    // 기존 데이터와 합치기
    const allData = [
      ...headerRow,
      ...emptyRow,
      ...columnHeaders,
      ...excelDataArray
    ];
    
    // 새로운 워크시트 생성
    const wsWithHeader = XLSX.utils.aoa_to_sheet(allData);
    
    // 컬럼 너비 설정 (첫 번째 행은 제외하고 데이터 컬럼만)
    const colWidthsFinal = [
      { wch: 8 },   // 번호
      { wch: 15 },  // 구독 결제 번호
      { wch: 30 },  // 상품명
      { wch: 15 },  // 등록 코드
      { wch: 25 },  // 멤버 회사
      { wch: 15 },  // 멤버 이름
      { wch: 12 },  // 가격
      { wch: 12 },  // 시작일
      { wch: 12 },  // 종료일
      { wch: 10 },  // 남은 일수
      { wch: 12 },  // 사용 인원
      { wch: 15 },  // 담당자
      { wch: 15 },  // 담당자 연락처
      { wch: 25 },  // 멤버 이메일
      { wch: 25 },  // 담당자 이메일
      { wch: 20 }   // 특이사항
    ];
    wsWithHeader['!cols'] = colWidthsFinal;



    // 워크시트를 워크북에 추가
    XLSX.utils.book_append_sheet(wb, wsWithHeader, '플랜 목록');

    // 엑셀 파일 생성 및 다운로드 (스타일 포함)
    const excelBuffer = XLSX.write(wb, { 
      bookType: 'xlsx', 
      type: 'array',
      cellStyles: true
    });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
    // 현재 날짜를 파일명에 포함
    const dateStr = currentDate.getFullYear() + 
                   String(currentDate.getMonth() + 1).padStart(2, '0') + 
                   String(currentDate.getDate()).padStart(2, '0');
    
    saveAs(data, `플랜목록_${dateStr}.xlsx`);
    } catch (error) {
      console.error('엑셀 다운로드 중 오류 발생:', error);
      alert('엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setIsExcelLoading(false);
    }
  };

  // 쿠폰 목록 API 호출 함수
  const fetchCouponList = async (selectedValue) => {
    try {
      const response = await axios.get('/api/member/accessCouponList', {
        params: { value: selectedValue } // 쿼리 파라미터로 전달
      });
      setTotalData([...response.data.data]); // 테이블 리스트 업데이트
      if (filterChange) {
        setExpandedRows({}); // 필터 변경 시에만 초기화
        console.log('fetchCouponList 함수에서 setExpandedRows 초기화 됨.')
        setFilterChange(false);
      }
      setDataChange(false);
    } catch (error) {
      console.error("쿠폰 목록 가져오기 실패:", error.message);
    }
  };

  // memberSelectedValue 변경 시마다 쿠폰 목록 갱신
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false; // 첫 로딩 후 false로 변경
      fetchCouponList(memberSelectedValue);
      return; // 필터 변경 플래그 설정하지 않음
    }

    setFilterChange(true);
    fetchCouponList(memberSelectedValue);
  }, [memberSelectedValue]);


  // 데이터 변경 시마다 쿠폰 목록 갱신
  useEffect(() => {
    if (dataChange === true) {
      fetchCouponList(memberSelectedValue);
    }
  }, [dataChange]);

  // 정렬 기준 변화
  const memberValueChange = (filter) => {
    let newValue = memberSelectedValue;

    switch (filter) {
      case "구독 결제 번호":
        newValue = memberSelectedValue === 1 ? 2 : 1;
        break;

      case "가격":
        newValue = memberSelectedValue === 3 ? 4 : 3;
        break;

      case "시작일":
        newValue = memberSelectedValue === 5 ? 6 : 5;
        break;

      case "종료일":
        // 7 : 종료일 임박순
        // 8 : 종료일 과거순
        // 9 : 종료일 미래순
        newValue = memberSelectedValue === 7 ? 9 : 7;
        break;

      default:
        console.warn("⚠️ 잘못된 필터값:", filter);
        return;
    }

    setMemberSelectedValue(newValue);
  };

  //search
  const searchItem = (filterKeyword) => {
    if (filterKeyword && filterKeyword !== '') {
      const filteredData = totalData.filter((item) => {
        return searchTargets.some((target) => {
          if (item[target] && typeof item[target] === 'string') {
            return item[target].toLowerCase().includes(filterKeyword.toLowerCase());
          }
        })
      });
      // pagination filter data slice
      setCurrentPost(filteredData.slice(page*postPerPage-postPerPage, page*postPerPage));
      setDataCnt(filteredData.length);
    } else {
      // pagination total data slice
      // setCurrentPost(totalData.slice(page*postPerPage-postPerPage, page*postPerPage));
      setCurrentPost((totalData || []).slice(page*postPerPage-postPerPage, page*postPerPage));

      setDataCnt(totalData.length);
    }
  }

  useEffect(() => {
    searchItem(filterKeyword)
  }, [totalData, page, filterKeyword])

  const handlePageChange = (page) => {
    setPage(page);
  };

  useEffect(() => {
    if (totalData && totalData.length > 0) {
      setCurrentPost(totalData.slice(indexOfFirstPost, indexOfLastPost));
      setDataCnt(totalData.length);
    } else {
      setDataCnt(1);
    }
  }, [totalData, page]);

  const getMoreInfo = async (paid_seq) => {
    try {
      const response = await axios.get(`/api/admin/subMember?paid_seq=${paid_seq}`);
      console.log(response);
      return response.data.data;
    } catch (error) {
      console.error("getMoreInfo error:", error);
      return [];
    }
  };

  const handleEditIconClick = (event, item, clickPart, data) => {
    // 이벤트 전파 중단 (Row 클릭 이벤트와 분리)
    event.stopPropagation();

    setPaidSeq(item.paid_seq);
    setPaidNote(item.note);
    setEditPart(clickPart);
    setPrevData(data);

    setPopupOpen(true);
  };

  const handleRowClick = async (rowPaidSeq) => {
    if (expandedRows[rowPaidSeq]) {
      // 이미 열려 있다면 닫기
      setExpandedRows((prev) => {
        const updatedRows = { ...prev };
        delete updatedRows[rowPaidSeq];
        return updatedRows;
      });
    } else {
      // 새 데이터를 가져와서 열기
      const data = await getMoreInfo(rowPaidSeq);
      setExpandedRows((prev) => ({ ...prev, [rowPaidSeq]: data }));
    }
  };

  useEffect(() => {
    if (!rowdataChange) return; // 변경이 없으면 실행 안 함

    const fetchData = async () => {
      const data = await getMoreInfo(paidSeqSelect);

      setExpandedRows((prev) => ({
        ...prev,
        [paidSeqSelect]: data, // 기존 값 유지 + 새로운 데이터 추가
      }));

      setRowDataChange(false);
      setPaidSeqSelect(false);
    };

    fetchData();
  }, [rowdataChange]);

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      {/* 다운로드 날짜 및 디데이 기준일 표시 */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        alignItems: 'center',
        marginBottom: '20px',
        padding: '0 20px'
      }}>
        
        <button
          onClick={() => downloadExcel().catch(error => {
            console.error('엑셀 다운로드 실패:', error);
            alert('엑셀 다운로드 중 오류가 발생했습니다.');
          })}
          disabled={totalData.length === 0 || isExcelLoading}
          style={{
            backgroundColor: isExcelLoading ? '#666' : '#38761D',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            padding: '10px 20px',
            cursor: (totalData.length === 0 || isExcelLoading) ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            opacity: (totalData.length === 0 || isExcelLoading) ? 0.6 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            minWidth: '180px',
            justifyContent: 'center'
          }}
        >
          {isExcelLoading ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #ffffff',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              엑셀 형성 중...
            </>
          ) : (
            `엑셀 다운로드 (${(() => {
              const activeData = (filterKeyword && filterKeyword !== '' ? 
                totalData.filter((item) => {
                  return searchTargets.some((target) => {
                    if (item[target] && typeof item[target] === 'string') {
                      return item[target].toLowerCase().includes(filterKeyword.toLowerCase());
                    }
                  })
                }) : totalData).filter((item) => {
                  const isValidDate = (dateStr) =>
                    dateStr && !isNaN(new Date(dateStr).getTime());
                  const isEnd = isValidDate(item.end_date)
                    ? new Date(item.end_date.split("T")[0]) < new Date()
                    : false;
                  return !isEnd;
                }).length;
              return activeData;
            })()}건)`
          )}
        </button>
      </div>

      <TableContainer>
        <Table
          style={{ tableLayout: "fixed", width: "100%", cursor: "pointer" }}
        >
          <colgroup>
            <col style={{ width: "12%" }} />
            <col style={{ width: "25%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "12%" }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <P.TableCellHead>
                <P.TableCellHeadDiv>
                  구독 결제 번호
                  {memberSelectedValue === 1 && <P.sortBtn src={sortDesc || ""} alt="sortDesc" style={{opacity : 1}}
                                                           onClick={() => {memberValueChange("구독 결제 번호");}}/>}
                  {memberSelectedValue === 2 && <P.sortBtn src={sortAsc || ""} alt="sortAsc" style={{opacity : 1}}
                                                           onClick={() => {memberValueChange("구독 결제 번호");}}/>}
                  {(memberSelectedValue !== 1 && memberSelectedValue !== 2) && (<P.sortBtn src={sortDesc || ""} alt="sortDesc"
                                                                                           onClick={() => {memberValueChange("구독 결제 번호");}}/>)}
                </P.TableCellHeadDiv>
              </P.TableCellHead>

              <P.TableCellHead>상품명</P.TableCellHead>

              <P.TableCellHead>사용 인원</P.TableCellHead>

              <P.TableCellHead>
                <P.TableCellHeadDiv>
                  가격
                  {memberSelectedValue === 3 && <P.sortBtn src={sortDesc || ""} alt="sortDesc" style={{opacity : 1}}
                                                           onClick={() => {memberValueChange("가격");}}/>}
                  {memberSelectedValue === 4 && <P.sortBtn src={sortAsc || ""} alt="sortAsc" style={{opacity : 1}}
                                                           onClick={() => {memberValueChange("가격");}}/>}
                  {(memberSelectedValue !== 3 && memberSelectedValue !== 4) && (<P.sortBtn src={sortDesc || ""} alt="sortDesc"
                                                                                           onClick={() => {memberValueChange("가격");}}/>)}
                </P.TableCellHeadDiv>
              </P.TableCellHead>

              <P.TableCellHead>
                <P.TableCellHeadDiv>
                  시작일
                  {memberSelectedValue === 5 && <P.sortBtn src={sortDesc || ""} alt="sortDesc" style={{opacity : 1}}
                                                           onClick={() => {memberValueChange("시작일");}}/>}
                  {memberSelectedValue === 6 && <P.sortBtn src={sortAsc || ""} alt="sortAsc" style={{opacity : 1}}
                                                           onClick={() => {memberValueChange("시작일");}}/>}
                  {(memberSelectedValue !== 5 && memberSelectedValue !== 6) && (<P.sortBtn src={sortDesc || ""} alt="sortDesc"
                                                                                           onClick={() => {memberValueChange("시작일");}}/>)}
                </P.TableCellHeadDiv>
              </P.TableCellHead>

              <P.TableCellHead>
                <P.TableCellHeadDiv>
                  종료일
                  {memberSelectedValue === 7 && <P.sortBtn src={sortAsc || ""} alt="sortAsc" style={{opacity : 1}}
                                                           onClick={() => {memberValueChange("종료일");}}/>}
                  {memberSelectedValue === 9 && <P.sortBtn src={sortDesc || ""} alt="sortDesc" style={{opacity : 1}}
                                                           onClick={() => {memberValueChange("종료일");}}/>}
                  {(memberSelectedValue !== 7 && memberSelectedValue !== 9) && (<P.sortBtn src={sortAsc || ""} alt="sortAsc"
                                                                                           onClick={() => {memberValueChange("종료일");}}/>)}
                </P.TableCellHeadDiv>
              </P.TableCellHead>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentPost &&
              currentPost.map((item) => {
                // isEnd 변수를 item 내에서 계산
                const isValidDate = (dateStr) =>
                  dateStr && !isNaN(new Date(dateStr).getTime());
                const isEnd = isValidDate(item.end_date)
                  ? new Date(item.end_date.split("T")[0]) < new Date()
                  : false;

                // JSX 반환
                return (
                  <React.Fragment key={item.num}>
                    <TableRow
                      onClick={() => handleRowClick(item.paid_seq)}
                      style={{ cursor: isEnd ? "default" : "pointer" }}
                    >
                      <P.TableCellEdit>{item.paid_seq}</P.TableCellEdit>
                      <P.TableCellEdit>
                        <P.EditableDiv>
                          <p>{item.note}</p>
                          {!isEnd && (
                            <P.FaRegEditIcon
                              onClick={(e) =>
                                handleEditIconClick(
                                  e,
                                  item,
                                  "상품명",
                                  item.note,
                                )
                              }
                            />
                          )}
                        </P.EditableDiv>
                      </P.TableCellEdit>
                      <P.TableCellEdit>
                        {item.end_date && isEnd ? (
                          <P.EndP>종료</P.EndP> // 종료된 경우
                        ) : (
                          <>
                            {item.use_member_cnt}/{item.usable_member_cnt}{" "}
                            {/* 사용 가능 */}
                          </>
                        )}
                      </P.TableCellEdit>
                      <P.TableCellEdit>
                        <P.EditableDiv
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            paddingRight: "60px",
                          }}
                        >
                          <p>{item.paid_price.toLocaleString()}</p>
                          {!isEnd && (
                            <P.FaRegEditIcon
                              onClick={(e) =>
                                handleEditIconClick(
                                  e,
                                  item,
                                  "가격",
                                  item.paid_price,
                                )
                              }
                            />
                          )}
                        </P.EditableDiv>
                      </P.TableCellEdit>
                      <P.TableCellEdit>
                        {item.issue_date
                          ? item.issue_date.slice(0, 10)
                          : "없음"}
                      </P.TableCellEdit>
                      <P.TableCellEdit>
                        <P.EditableDiv>
                          <p>{item.end_date?.slice(0, 10) ?? "없음"}</p>
                          {!isEnd && (
                            <P.FaRegEditIcon
                              onClick={(e) =>
                                handleEditIconClick(
                                  e,
                                  item,
                                  "종료일",
                                  item.end_date,
                                )
                              }
                            />
                          )}
                        </P.EditableDiv>
                      </P.TableCellEdit>
                    </TableRow>

                    {/*자세히 보기*/}
                    <CouponListTableMore
                      item={item}
                      expandedData={expandedRows[item.paid_seq]}
                      isEnd={isEnd}
                    />
                  </React.Fragment>
                );
              })}
          </TableBody>
        </Table>

        <RealTableFooter style={{ display: "flex", justifyContent: "center" }}>
          <Pagination
            activePage={page}
            itemsCountPerPage={postPerPage}
            totalItemsCount={dataCnt}
            pageRangeDisplayed={5}
            prevPageText={"<"}
            nextPageText={">"}
            onChange={handlePageChange}
          />
        </RealTableFooter>
      </TableContainer>

      {/* 수정 팝업*/}
      {popupOpen ? (
        <EditContextPopup
          closePopup={() => setPopupOpen(false)}
          paidSeq={paidSeq}
          paidNote={paidNote}
          editPart={editPart}
          prevData={prevData}
        />
      ) : null}
    </>
  );
}

const RealTableFooter = styled(TableFooter)`
    display: flex;
    justify-content: center;
    text-align: center;
`;
